/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { Component, OnInit, inject } from '@angular/core';
import { firstValueFrom, map, Observable, Subject, takeUntil } from 'rxjs';
import { FSModelBackendService } from './service/fund-strategy-backend.service';
import { EditStatus } from './pojo/fund-strategy-enums';
import {
  FS_SUGGESTED_CARRY_PCNT,
  FS_SUGGESTED_EXPECTED_EXPENSES,
  FS_SUGGESTED_TOTAL_COMMITMENT,
  SUGGESTED_GROUPS,
} from './pojo/fund-strategy-constants';
import { GroupFS, FundStrategyModel, SaveModelEmitterDTO, FSFundMetricsDTO, FSFundMetricsMiniDTO, FundStrategyExportExcelData,
  FsAnalyticsData }
  from './pojo/fund-strategy-export-classes';
import { FundStrategyService } from './service/fund-strategy-service';
import { FundStrategyFormService } from './service/fund-strategy-form-group';
import { FsAnalyticsCalcService } from './fund-strategy-analytics/calc-service/fs-analytics-calc.service';
import { CanDeactivateComponent } from '../../shared/component/can-deactivate-guard/can-deactivate-guard-interface';
import { ExcelTemplateName } from '../../excel/interfaces';
import { ExcelService } from '../../excel';
import { FundStrategySelectorComponent } from "./fund-strategy-selector/fund-strategy-selector.component";
import {
  FundStrategySettingsInputsComponent,
} from "./fund-strategy-settings-inputs/fund-strategy-settings-inputs.component";
import { FundStrategyTableComponent } from "./fund-strategy-table/fund-strategy-table.component";
import { FundStrategyAnalyticsComponent } from "./fund-strategy-analytics/fund-strategy-analytics.component";
import { FundStrategyChartsComponent } from "./fund-strategy-charts/fund-strategy-charts.component";
import { OrganizationDTO } from "../../organization/dto/OrganizationDTO";
import {
  UnsavedChangesModalService,
} from "../../shared/component/unsaved-changes-modal-wrapper/service/unsaved-changes-modal.service";
import { OrganizationsService } from "../../organization/service/organizations.service";
import { FsLoadingStateComponent } from "./fs-loading-state/fs-loading-state.component";

@Component({
  selector: 'app-fund-strategy',
  templateUrl: './fund-strategy.component.html',
  styleUrls: ['./fund-strategy.component.scss'],
  imports: [
    FundStrategySelectorComponent,
    FundStrategySettingsInputsComponent,
    FundStrategyTableComponent,
    FundStrategyAnalyticsComponent,
    FundStrategyChartsComponent,
    FsLoadingStateComponent,
  ],
})

export class FundStrategyComponent implements OnInit, CanDeactivateComponent {
  private fsModelBackendService = inject(FSModelBackendService);
  private fsModelService = inject(FundStrategyService);
  private formCreationService = inject(FundStrategyFormService);
  private unsavedChangesModalService = inject(UnsavedChangesModalService);
  private organizationsService = inject(OrganizationsService);
  private excelService = inject(ExcelService);

  private _destroyStream: Subject<void> = new Subject<void>();

  selectedOrganization: OrganizationDTO;
  modelName = '';
  shouldOpenRename = false;

  groupsFS: GroupFS[] = [];
  buttonsProps = {
    apply: true,
    cancel: true,
  };

  isModelBlocked = true;
  fsModels: FundStrategyModel[] = [];
  selectedModel = new FundStrategyModel();
  metrics = new FSFundMetricsDTO();
  dataForExportExcel: FundStrategyExportExcelData;
  fileNameForExportExcel = 'fund-strategy-group.xlsx';
  showRoundsTable = false;
  analyticsData = new FsAnalyticsData();
  loadingState = false;
  ngOnInit(): void {
    this.organizationsService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        if (!org) {
          return;
        }
        this.selectedOrganization = org;
        this.modelName = org.name;
        this.resetAllData();
        this.getModels();
      });
  }

  /**
   * Handle button events in selector.
   */

  async buttonClicked(status: EditStatus): Promise<void> {
    if (status === EditStatus.confirm) {
      this.saveModel(this.collectFsModelData());
    }
    if (status === EditStatus.decline) {
      this.selectedModel = this.fsModels.find((model) => model.name === this.selectedModel.name);
      if (this.selectedModel) {
        this.isModelBlocked = false;
        this.selectedModelChanged(this.selectedModel);
      }
      this.shouldOpenRename = false;
    }
    if (status === EditStatus.delete) {
      await this.fsModelBackendService.deleteModel(this.selectedModel.uuid);
      this.resetAllData();
      this.getModels();
    }
    if (status === EditStatus.newModel) {
      this.isModelBlocked = true;
    }
    if (status === EditStatus.copyModel) {
      const fsModel = new FundStrategyModel();
      fsModel.orgUUID = this.selectedOrganization.uuid;
      fsModel.name = this.selectedModel.name + ' (copy)';
      fsModel.groups = this.selectedModel.groups;
      fsModel.totalCommitment = this.selectedModel.totalCommitment;
      fsModel.expectedExpenses = this.selectedModel.expectedExpenses;
      fsModel.carryInterestPcnt = this.selectedModel.carryInterestPcnt;
      fsModel.groups.forEach((gr) => {
        gr.uuid = null;
        gr.subGroups.forEach((sb) => {
          sb.uuid = null;
          sb.roundsData.forEach((r) => r.uuid = null);
        });
      });
      this.modelName = fsModel.name; // to choose the copied model
      await this.saveModel(fsModel);
      this.shouldOpenRename = true;
    }
    if (status === EditStatus.renameModel) {
      this.selectedModel = await firstValueFrom(this.fsModelBackendService
        .renameModel(this.selectedModel.uuid, this.modelName)
        .pipe(map(this.fsModelService.mapToFundStrategyModel)));
      this.sortSelectedModel();
      this.fsModels = this.fsModels.map((model) =>
        (model.uuid === this.selectedModel.uuid ? this.selectedModel : model),
      );
      this.shouldOpenRename = false;
    }

  }

  async saveModel(fsModel: FundStrategyModel): Promise<void> {
    this.selectedModel = await firstValueFrom(this.fsModelBackendService.saveNewModel(fsModel)
      .pipe(map((response) =>
        new FundStrategyModel(
          response.uuid,
          response.name,
          response.totalCommitment,
          response.expectedExpenses,
          response.carryInterestPcnt,
          response.orgUUID,
          response.groups)),
      ),
    );
    this.getModels();
  }

  async getModels(): Promise<void> {
    this.loadingState = true;
    this.fsModels = await this.fsModelBackendService.getAllByOrg(this.selectedOrganization.uuid);
    if (this.fsModels.length) {
      this.fsModels = this.fsModelService.setLastSaveText(this.fsModels);
      this.selectedModel = this.fsModels.find((model) => model.name === this.modelName) || this.fsModels[0];
      this.isModelBlocked = false;

      // sorting should be done before calculations of portfolio metrics
      this.sortSelectedModel();
      this.prepareGroupsFS();
    } else {
      this.selectedModel = null;
      this.isModelBlocked = true;
    }
    setTimeout(() => {
      this.loadingState = false;
    }, 400); // set minimal loading time
  }


  async handleSaveGroups(event: SaveModelEmitterDTO): Promise<void> {
    this.groupsFS = event.groups;

    const updateModel = async (updateFunction: Observable<any>): Promise<void> => {
      this.selectedModel = await firstValueFrom(updateFunction.pipe(map(this.fsModelService.mapToFundStrategyModel)));
      this.sortSelectedModel();
      this.updateFsModels();
      this.prepareGroupsFS();
      this.calculateMetrics();
    };

    switch (event.message) {
      case EditStatus.addGroup:
        if (this.selectedModel.name) {
          await updateModel(this.fsModelBackendService.addGroupToModel(this.selectedModel.uuid, event.groups[event.grIndex]));
        }
        break;
      case EditStatus.duplicateGroup:
        await updateModel(this.fsModelBackendService.duplicateGroup(event.groups[event.grIndex].uuid));
        break;
      case EditStatus.updateGroupName:
        await updateModel(this.fsModelBackendService.updateGroupName(this.selectedModel.groups[event.grIndex].uuid, event.groups[event.grIndex].name));
        break;
      case EditStatus.updateSubGroupName:
        await updateModel(this.fsModelBackendService.updateSubGroupName(this.selectedModel.groups[event.grIndex].subGroups[event.sbIndex].uuid, event.groups[event.grIndex].subGroups[event.sbIndex].name));
        break;
      case EditStatus.addSubGroup:
        event.groups[event.grIndex].subGroups[event.sbIndex].uuid = null;
        await updateModel(this.fsModelBackendService.addSubGroup(this.selectedModel.groups[event.grIndex].uuid, event.groups[event.grIndex].subGroups[event.sbIndex]));
        break;
      case EditStatus.updateSubGroup:
        await updateModel(this.fsModelBackendService.updateSubGroup(event.groups[event.grIndex].subGroups[event.sbIndex]));
        break;
      case EditStatus.duplicateSubGroup:
        event.groups[event.grIndex].subGroups[event.sbIndex].roundsData.forEach((r) => r.uuid = null);
        await updateModel(this.fsModelBackendService.duplicateSubGroup(event.groups[event.grIndex].subGroups[event.sbIndex].uuid));
        break;
      case EditStatus.deleteGroup:
        await this.fsModelBackendService.deleteFundStrategyGroup(this.selectedModel.groups[event.grIndex].uuid);
        this.selectedModel.groups = event.groups;
        break;
      case EditStatus.deleteSubGroup:
        await updateModel(this.fsModelBackendService.deleteFundStrategySubGroup(this.selectedModel.groups[event.grIndex].subGroups[event.sbIndex].uuid));
        break;
      case EditStatus.updateSubGroupOrder:
        await updateModel(this.fsModelBackendService.updateSubGroupOrder(this.selectedModel.groups[event.grIndex].subGroups[event.sbIndex].uuid, event.newOrder));
        break;
      case EditStatus.updateGroupOrder:
        await updateModel(this.fsModelBackendService.updateGroupOrder(this.selectedModel.groups[event.grIndex].uuid, event.newOrder));
        break;
    }
  }

  async selectedModelChanged(model: FundStrategyModel): Promise<void> {
    this.selectedModel = await firstValueFrom(this.fsModelBackendService.getModelByUUID(model.uuid));
    this.selectedModel.lastSaveText = this.fsModelService.formatAndSetLastSaveText(this.selectedModel);
    this.sortSelectedModel();
    // TODO: make sorting at the backend
    this.prepareGroupsFS();
  }

  async saveMetrics(metricsData: FSFundMetricsMiniDTO): Promise<void> {
    await firstValueFrom(this.fsModelBackendService.saveMetrics(this.selectedModel.uuid, metricsData));
    // after metrics update we don't get the updated model from the backend, so we need to update it manually
    this.metrics.totalCommitment = this.selectedModel.totalCommitment = metricsData.totalCommitment;
    this.metrics.expectedExpenses = this.selectedModel.expectedExpenses = metricsData.expectedExpenses;
    this.metrics.carryInterestPcnt = this.selectedModel.carryInterestPcnt = metricsData.carryInterestPcnt;
  }

  calculateMetrics(): void {
    this.analyticsData = FsAnalyticsCalcService.calculateAnalyticsData(this.groupsFS);
    this.metrics = FsAnalyticsCalcService.createFundMetrics(this.analyticsData, this.selectedModel);
  }


  /**
   * Prevent leaving the page when user has unsaved changes
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (this.unsavedChangesModalService.getIsInEditMode()) {
      return this.unsavedChangesModalService.showConfirmDialog();
    }
    return true;
  }

  saveDataForExportExcel(data: FundStrategyExportExcelData): void {
    this.dataForExportExcel = data;
  }

  exportExcel(): void {
    this.excelService.exportByTemplate(this.dataForExportExcel, this.fileNameForExportExcel, ExcelTemplateName.fundStrategyExternal);
  }

  /**
   * Update all models array with the last updated model data.
   * Needed to update 'lastSaveText' and sort models in selector
   * Find the index of the model that needs to be replaced.
   * Replace the model at the found index with the updated selectedModel
   */
  private updateFsModels(): void {
    const index = this.fsModels.findIndex((model) => model.uuid === this.selectedModel.uuid);
    if (index !== -1) {
      this.fsModels[index] = this.selectedModel;
    }
    this.fsModels = this.fsModelService.setLastSaveText(this.fsModels);
  }

  private collectFsModelData(): FundStrategyModel {
    const fsModel = new FundStrategyModel();
    fsModel.orgUUID = this.selectedOrganization.uuid;
    fsModel.name = this.modelName;
    fsModel.totalCommitment = FS_SUGGESTED_TOTAL_COMMITMENT;
    fsModel.expectedExpenses = FS_SUGGESTED_EXPECTED_EXPENSES;
    fsModel.carryInterestPcnt = FS_SUGGESTED_CARRY_PCNT;

    fsModel.groups = SUGGESTED_GROUPS;
    return fsModel;
  }

  // TODO: make sorting at the backend
  private sortSelectedModel(): void {
    if (this.selectedModel?.groups) {
      this.selectedModel.groups.sort((a, b) => a.grOrder - b.grOrder);

      this.selectedModel.groups.forEach((group) => {
        group.subGroups.sort((a, b) => a.subGrOrder - b.subGrOrder);

        group.subGroups.forEach((subGroup) => {
          subGroup.roundsData.sort((a, b) => a.roundOrder - b.roundOrder);
        });
      });
    }
  }

  private prepareGroupsFS(): void {
    this.groupsFS = this.formCreationService.fillFormFromFs(this.selectedModel.groups).controls.fundStrategy.value as GroupFS[];
    this.calculateMetrics();
  }

  private resetAllData(): void {
    this.fsModels = null;
    this.selectedModel = null;
  }
}
